import React, { useState, useEffect } from "react";
import { Col, Row, Card, Table, Button, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? window.location.origin : 'https://sgrloto.com';

export default function RapportJournalier() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentSearchQuery, setAgentSearchQuery] = useState("");

  // Date picker states (Default to today)
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [confirmedStart, setConfirmedStart] = useState(todayStr);
  const [confirmedEnd, setConfirmedEnd] = useState(todayStr);

  const fetchStats = async (start, end) => {
    setLoading(true);
    const token = localStorage.getItem("access_token");
    const headers = token ? { "Authorization": `Bearer ${token}` } : {};

    try {
      const url = `${API_BASE}/api/dashboard/stats/?start_date=${start}&end_date=${end}`;
      const statsRes = await fetch(url, { headers });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Stats API error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(confirmedStart, confirmedEnd);
  }, [confirmedStart, confirmedEnd]);

  const handleValidateDates = () => {
    setConfirmedStart(startDate);
    setConfirmedEnd(endDate);
  };

  const formatVal = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? "0" : String(Math.round(num));
  };

  const activePOS = stats?.active_agents ?? 0;

  // Filter agent performance data
  const agentList = stats?.agent_stats || [];
  const filteredAgents = agentList.filter(agent => {
    const query = agentSearchQuery.toLowerCase();
    const username = (agent.username || "").toLowerCase();
    const firstName = (agent.first_name || "").toLowerCase();
    const lastName = (agent.last_name || "").toLowerCase();
    const agentId = (agent.id || "").toLowerCase();
    return username.includes(query) || firstName.includes(query) || lastName.includes(query) || agentId.includes(query);
  });

  const rowColor = (balance) => (parseFloat(balance) < 0 ? '#DC3545' : '#28A745');

  const agentTotals = filteredAgents.reduce((acc, agent) => ({
    tickets_count: acc.tickets_count + (parseInt(agent.tickets_count, 10) || 0),
    sales: acc.sales + (parseFloat(agent.sales) || 0),
    payouts: acc.payouts + (parseFloat(agent.payouts) || 0),
    commissions: acc.commissions + (parseFloat(agent.commissions) || 0),
    sup_commissions: acc.sup_commissions + (parseFloat(agent.sup_commissions) || 0),
    b_final: acc.b_final + (parseFloat(agent.b_final) || 0),
  }), { tickets_count: 0, sales: 0, payouts: 0, commissions: 0, sup_commissions: 0, b_final: 0 });

  return (
    <div style={{ padding: '24px 0' }}>
      <h3 className="text-center mb-4" style={{ fontWeight: 'bold', color: '#222' }}>Rapport journalier</h3>

      {/* Date Pickers */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '16px' }}>
        <Row className="mb-3">
          <Col xs={12} md={6} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Début</label>
            <div className="position-relative d-flex align-items-center">
              <input
                type="date"
                className="form-control"
                style={{
                  borderColor: '#28A745',
                  paddingRight: '36px',
                  borderRadius: '4px'
                }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
            </div>
          </Col>
          <Col xs={12} md={6}>
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Fin</label>
            <div className="position-relative d-flex align-items-center">
              <input
                type="date"
                className="form-control"
                style={{
                  borderColor: '#28A745',
                  paddingRight: '36px',
                  borderRadius: '4px'
                }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
            </div>
          </Col>
        </Row>
        <Button
          className="w-100"
          style={{
            backgroundColor: '#0069D9',
            borderColor: '#0062cc',
            color: '#FFF',
            fontWeight: '600',
            padding: '8px 0',
            borderRadius: '4px'
          }}
          onClick={handleValidateDates}
        >
          Valider
        </Button>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="ms-2 text-muted small">Chargement...</span>
        </div>
      ) : (
        <>
          {/* Main Daily Report Card */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
            <div className="mb-4" style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#333' }}>
              <div>Début : <strong>{confirmedStart}</strong></div>
              <div>Fin : <strong>{confirmedEnd}</strong></div>
              <div>Quantité POS : <strong>{activePOS}</strong></div>
            </div>

            <h5 className="mb-2" style={{ fontWeight: 'bold', color: '#111' }}>Rapport journalier</h5>
            
            <div className="table-responsive">
              <Table bordered className="mb-0 text-center" style={{ borderColor: '#EAEAEA', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#FAF9F9', fontWeight: 'bold' }}>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333' }}></th>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333' }}>Total ventes</th>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333' }}>A payé</th>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333' }}>Pourcentage</th>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 1 */}
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>TFiche : <strong>{stats?.tickets_count ?? 0}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>Vente : <strong>{formatVal(stats?.total_sales)}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>A payé : <strong>{formatVal(stats?.total_payout)}</strong></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px' }}></td>
                  </tr>
                  {/* Row 2 */}
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>%Agent : <strong>{formatVal(stats?.total_commissions)}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500', color: '#28A745' }}>Balance Sans %agent : <strong>{Math.round((parseFloat(stats?.total_sales || 0) - parseFloat(stats?.total_payout || 0)) || 0)}</strong></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500', color: '#28A745' }}>Balance Avec %agent : <strong>{formatVal(stats?.net_profit)}</strong></td>
                  </tr>
                  {/* Row 3 */}
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>%Superviseur : <strong>0</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500', color: '#28A745' }}>Balance Sans %superviseur : <strong>{Math.round((parseFloat(stats?.total_sales || 0) - parseFloat(stats?.total_payout || 0)) || 0)}</strong></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500', color: '#28A745' }}>Balance Avec %superviseur : <strong>{Math.round((parseFloat(stats?.total_sales || 0) - parseFloat(stats?.total_payout || 0)) || 0)}</strong></td>
                  </tr>
                  {/* Row 4 */}
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>Fiche gagnante : <strong>{stats?.winning_tickets_count ?? 0}</strong></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px' }}></td>
                  </tr>
                  {/* Row 5 */}
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem', textAlign: 'left', fontWeight: '500' }}>Fiche supprimée : <strong>{stats?.cancelled_tickets_count ?? 0}</strong></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px', borderRight: '1px solid #EAEAEA' }}></td>
                    <td style={{ padding: '12px' }}></td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Card>

          {/* Aperçu par succursale */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
            <Card.Header className="bg-white border-0 p-0 mb-3">
              <h4 style={{ fontWeight: 'bold', margin: 0, color: '#333' }}>Aperçu par succursale</h4>
            </Card.Header>
            <div className="table-responsive">
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#DDD' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8F9FA' }}>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Succursale <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Total ventes <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      A payé <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Pourcentage <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Balance <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(!stats?.branch_stats || stats.branch_stats.length === 0) ? (
                    <tr>
                      <td colSpan={5} className="text-center py-3 text-muted">
                        Aucune donnée par succursale disponible.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {stats.branch_stats.map((branch, idx) => {
                        const sales = parseFloat(branch.sales) || 0;
                        const payouts = parseFloat(branch.payouts) || 0;
                        const balance = parseFloat(branch.balance) || 0;
                        const pct = sales > 0 ? ((payouts / sales) * 100).toFixed(1) : '0.0';
                        const color = balance < 0 ? '#DC3545' : '#28A745';
                        return (
                          <tr key={idx} style={{ verticalAlign: 'middle' }}>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                              {branch.name}
                            </td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(branch.sales)}
                            </td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(branch.payouts)}
                            </td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'center' }}>
                              {pct}%
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                              {formatVal(branch.balance)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Ligne totaux */}
                      {(() => {
                        const totSales = stats.branch_stats.reduce((a, b) => a + (parseFloat(b.sales) || 0), 0);
                        const totPayouts = stats.branch_stats.reduce((a, b) => a + (parseFloat(b.payouts) || 0), 0);
                        const totBalance = stats.branch_stats.reduce((a, b) => a + (parseFloat(b.balance) || 0), 0);
                        const totPct = totSales > 0 ? ((totPayouts / totSales) * 100).toFixed(1) : '0.0';
                        const totColor = totBalance < 0 ? '#DC3545' : '#28A745';
                        return (
                          <tr style={{ verticalAlign: 'middle', backgroundColor: '#F8F9FA', fontWeight: 'bold' }}>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'left' }}>Total</td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{Math.round(totSales)}</td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{Math.round(totPayouts)}</td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'center' }}>{totPct}%</td>
                            <td style={{ fontSize: '0.85rem', color: totColor, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{Math.round(totBalance)}</td>
                          </tr>
                        );
                      })()}
                    </>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* New Agents Performance Card at the bottom */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
            <Card.Header className="bg-white border-0 p-0 mb-3">
              <h4 style={{ fontWeight: 'bold', margin: 0, color: '#333' }}>Agents</h4>
            </Card.Header>

            {/* Action buttons + Search */}
            <div className="pb-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
              <div className="d-flex" style={{ gap: '4px' }}>
                {["COPIER", "EXCEL", "PDF", "IMPRIMER"].map((label, idx) => (
                  <button
                    key={idx}
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: '1px solid #CCCCCC',
                      color: '#333333',
                      fontSize: '0.78rem',
                      padding: '6px 16px',
                      borderRadius: '2px',
                      height: '34px',
                      fontWeight: 'normal',
                      outline: 'none'
                    }}
                    onClick={() => alert(`${label} cliqué`)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <span className="fw-bold small text-dark" style={{ fontSize: '0.92rem' }}>Search:</span>
                <input 
                  type="text" 
                  className="form-control form-control-sm"
                  style={{ width: '220px', borderRadius: '4px', border: '1px solid #CCC' }} 
                  value={agentSearchQuery}
                  onChange={(e) => setAgentSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Table with borders matching the other SGR grids */}
            <div className="table-responsive">
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#DDD' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8F9FA' }}>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Tfiche <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Vente <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      A payé <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      %Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      %Sup <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Balance <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-3 text-muted">
                        Aucune performance agent trouvée pour ce jour.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredAgents.map((agent, idx) => {
                        const color = rowColor(agent.b_final);
                        return (
                          <tr key={idx} style={{ verticalAlign: 'middle' }}>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'left' }}>
                              {agent.first_name || agent.last_name ? `${agent.first_name} ${agent.last_name}` : agent.username}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'center' }}>
                              {agent.tickets_count}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.sales)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.payouts)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.commissions)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.sup_commissions)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.b_final)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ verticalAlign: 'middle', backgroundColor: '#F8F9FA', fontWeight: 'bold' }}>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'left' }}>
                          Total
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'center' }}>
                          {agentTotals.tickets_count}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.sales)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.payouts)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.commissions)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.sup_commissions)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.b_final)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
